import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import { ProductCard } from "../components/ProductCard";
import { ConnectState } from "../components/ConnectState";
import { NewsletterForm } from "../components/NewsletterForm";
import { useProducts } from "../lib/api";
import { PreviewBanner } from "../components/PreviewBanner";

const HERO_IMG = "/images/hero-model.png";
const STORY_IMG = "/images/story-model.png";
const RUN_IMGS = [
  {
    url: "https://customer-assets-cm19k8pv.emergentagent.net/job_streetwear-lab-42/artifacts/9th4earf_IMG_5435.PNG",
    caption: "001 // Run club kit",
  },
  {
    url: "https://customer-assets-cm19k8pv.emergentagent.net/job_streetwear-lab-42/artifacts/1krl0jt0_IMG_5778.PNG",
    caption: "002 // Night miles layer",
  },
  {
    url: "https://customer-assets-cm19k8pv.emergentagent.net/job_streetwear-lab-42/artifacts/n5gwq8sf_IMG_6056.PNG",
    caption: "003 // Crew standard",
  },
];
const SOCIAL_IMGS = [
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_streetwear-lab-42/artifacts/u7xtor1t_IMG_6434.PNG",
    href: "https://instagram.com/trailnone",
    platform: "IG",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_streetwear-lab-42/artifacts/xhd35k87_IMG_6490.PNG",
    href: "https://instagram.com/trailnone",
    platform: "IG",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_streetwear-lab-42/artifacts/1krl0jt0_IMG_5778.PNG",
    href: "https://tiktok.com/@trailnone",
    platform: "TT",
  },
  {
    src: "https://customer-assets-cm19k8pv.emergentagent.net/job_streetwear-lab-42/artifacts/9th4earf_IMG_5435.PNG",
    href: "https://tiktok.com/@trailnone",
    platform: "TT",
  },
];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const ProductSection = ({ eyebrow, title, linkTo, linkLabel, state, testId }) => (
  <section className="px-5 py-16 sm:px-10 lg:px-16 lg:py-24" data-testid={testId}>
    <Reveal>
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">
            {eyebrow}
          </p>
          <h2 className="mt-2 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl lg:text-4xl">
            {title}
          </h2>
        </div>
        <Link
          to={linkTo}
          data-testid={`${testId}-view-all`}
          className="group flex shrink-0 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-signal"
        >
          {linkLabel}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </Reveal>
    {state.loading ? (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="aspect-[4/5] animate-pulse bg-secondary" />
        ))}
      </div>
    ) : !state.connected && state.products.length === 0 ? (
      <ConnectState compact />
    ) : state.products.length === 0 ? (
      <p className="border border-border p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
        No products published yet — add them in Shopify
      </p>
    ) : (
      <>
        {!state.connected && <PreviewBanner />}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {state.products.slice(0, 4).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </>
    )}
  </section>
);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const newDrop = useProducts({ first: 4, sort: "newest" });
  const bestSellers = useProducts({ first: 4, sort: "best-selling" });

  return (
    <div>
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative flex h-[100svh] items-end overflow-hidden"
        data-testid="hero-section"
      >
        <motion.div style={{ y: bgY }} className="absolute -top-[15%] inset-x-0 h-[130%]">
          <img
            src={HERO_IMG}
            alt="TRAILNONE Own Path Garden Hoodie campaign"
            className="h-full w-full object-cover object-[74%_center] sm:object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/45 to-background/15" />
        </motion.div>
        <motion.div
          style={{ opacity: fade }}
          className="relative z-10 w-full px-5 pb-14 sm:px-10 lg:px-16 lg:pb-20"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-[11px] uppercase tracking-[0.35em] text-foreground/80"
          >
            TRAILNONE — For the ones who move different
          </motion.p>
          <h1 className="mt-4 font-display text-[16vw] font-bold uppercase leading-[0.85] tracking-tighter sm:text-[12vw] lg:text-[9.5vw]">
            <MaskedLine delay={0.35}>Follow</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="text-outline">Your own</span>
            </MaskedLine>
            <MaskedLine delay={0.65}>
              Path<span className="text-signal">.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-5 max-w-md font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground"
          >
            Lead with authenticity.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <Link
              to="/drops"
              data-testid="hero-shop-button"
              className="group flex items-center gap-2 bg-signal px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-signal/90"
            >
              Shop new drop
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/collections"
              data-testid="hero-explore-button"
              className="border border-foreground/40 px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] backdrop-blur-sm transition-colors hover:border-foreground hover:bg-foreground hover:text-background"
            >
              Explore collection
            </Link>
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-6 right-6 z-10 hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground sm:flex"
        >
          Scroll
          <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
        </motion.div>
      </section>

      <Marquee />

      {/* FEATURED / NEW DROP */}
      <ProductSection
        eyebrow="001 — Fresh off the trail"
        title="The latest drop"
        linkTo="/drops"
        linkLabel="All new drops"
        state={newDrop}
        testId="section-new-drop"
      />

      {/* BRAND STORY */}
      <section className="border-t border-border" data-testid="section-brand-story">
        <div className="grid lg:grid-cols-2">
          <div className="flex flex-col justify-center px-5 py-16 sm:px-10 lg:px-16 lg:py-28">
            <Reveal>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">
                002 — The manifesto
              </p>
              <h2 className="mt-4 font-display text-3xl font-bold uppercase leading-[0.95] tracking-tighter sm:text-4xl lg:text-5xl">
                Made for the
                <br />
                <span className="text-outline">self-made.</span>
              </h2>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
                TRAILNONE was built on a single conviction: the path doesn't
                exist until you cut it. Every piece is engineered heavy,
                washed hard, and finished with intent — no logos begging for
                attention, no trends on lease. Just uniform-grade streetwear
                for people who move on their own signal.
              </p>
              <div className="mt-8 space-y-3 font-mono text-xs uppercase tracking-[0.2em]">
                <p className="flex items-center gap-3">
                  <span className="text-signal">///</span> Follow your own path
                </p>
                <p className="flex items-center gap-3">
                  <span className="text-signal">///</span> Lead with authenticity
                </p>
                <p className="flex items-center gap-3">
                  <span className="text-signal">///</span> Create your own path
                </p>
              </div>
              <Link
                to="/about"
                data-testid="brand-story-link"
                className="group mt-10 inline-flex items-center gap-2 border border-foreground px-7 py-3.5 font-mono text-xs uppercase tracking-[0.25em] transition-colors hover:bg-foreground hover:text-background"
              >
                Read the story
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="relative min-h-[420px] overflow-hidden lg:min-h-0">
            <motion.img
              src={STORY_IMG}
              alt="TRAILNONE lookbook editorial"
              loading="lazy"
              initial={{ scale: 1.15 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute bottom-4 left-4 bg-background/80 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] backdrop-blur-sm">
              Lookbook — Vol. 01
            </div>
          </div>
        </div>
      </section>

      {/* BEST SELLERS */}
      <div className="border-t border-border">
        <ProductSection
          eyebrow="003 — Proven on pavement"
          title="Best sellers"
          linkTo="/shop"
          linkLabel="Shop all"
          state={bestSellers}
          testId="section-best-sellers"
        />
      </div>

      {/* RUN CLUB */}
      <section className="border-t border-border bg-card" data-testid="section-run-club">
        <div className="px-5 py-16 sm:px-10 lg:px-16 lg:py-24">
          <Reveal>
            <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">
                  004 — Community
                </p>
                <h2 className="mt-2 font-display text-3xl font-bold uppercase tracking-tighter sm:text-4xl lg:text-5xl">
                  TRAILNONE <span className="text-outline">Run Club</span>
                </h2>
              </div>
              <Link
                to="/run-club"
                data-testid="run-club-cta"
                className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-signal"
              >
                Join the crew
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <div className="grid gap-3 sm:grid-cols-3">
            {RUN_IMGS.map((img, i) => (
              <motion.div
                key={img.url}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="group relative aspect-[3/4] overflow-hidden"
              >
                <img
                  src={img.url}
                  alt={img.caption}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <p className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/90">
                  {img.caption}
                </p>
              </motion.div>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              Every week, in every city we touch, the crew runs at night. No
              pace requirements, no egos — just miles, music and the people
              who show up. Members get first access to drops, exclusive
              colorways and the reflective kit.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SOCIAL */}
      <section className="border-t border-border" data-testid="section-social">
        <div className="px-5 py-16 sm:px-10 lg:px-16 lg:py-24">
          <Reveal>
            <div className="mb-10 flex items-end justify-between">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">
                  005 — On the feed
                </p>
                <h2 className="mt-2 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl lg:text-4xl">
                  @trailnone
                </h2>
              </div>
              <div className="flex gap-6">
                <a
                  href="https://instagram.com/trailnone"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-follow-instagram"
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-signal"
                >
                  IG — @trailnone
                </a>
                <a
                  href="https://tiktok.com/@trailnone"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-follow-tiktok"
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-signal"
                >
                  TikTok — @trailnone
                </a>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 gap-px border border-border bg-border lg:grid-cols-4">
            {SOCIAL_IMGS.map((item, i) => (
              <motion.a
                key={item.src}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="group relative aspect-square overflow-hidden"
                data-testid={`social-tile-${i}`}
              >
                <img
                  src={item.src}
                  alt={`TRAILNONE on ${item.platform === "IG" ? "Instagram" : "TikTok"}`}
                  loading="lazy"
                  className="h-full w-full scale-[1.7] object-cover transition-transform duration-700 group-hover:scale-[1.85]"
                />
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="border-t border-border" data-testid="section-newsletter">
        <div className="grid lg:grid-cols-2">
          <div className="px-5 py-16 sm:px-10 lg:px-16 lg:py-24">
            <Reveal>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">
                006 — First access
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold uppercase leading-[0.95] tracking-tighter sm:text-4xl lg:text-5xl">
                Drops sell out.
                <br />
                <span className="text-outline">Don't miss one.</span>
              </h2>
            </Reveal>
          </div>
          <div className="flex flex-col justify-center border-t border-border px-5 py-16 sm:px-10 lg:border-l lg:border-t-0 lg:px-16">
            <Reveal delay={0.1}>
              <p className="mb-8 text-sm leading-relaxed text-muted-foreground">
                Email or SMS — you pick the channel. Early links, restock
                alerts, Run Club invites. No noise, ever.
              </p>
              <NewsletterForm source="homepage" />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
